/**
 * Screens drawn for the deck, in the app's own look, with made-up clients: Filing status, Today, the department's mail,
 * job documents, fees and Workbook. Where a real screen on a demo firm exists the deck shows that instead (BASE/*.webp).
 */
const chip = (t: string, c: string) => `<span class="p-chip ${c}">${t}</span>`;
const tile = (v: string, l: string, c = '') => `<div class="p-tile ${c}"><b>${v}</b><span>${l}</span></div>`;

const FILED = chip('Filed', 'ok'), REVIEW = chip('In review', 'vio'), PARTNER = chip('With partner', 'blu'), AWAIT = chip('Documents awaited', 'amb'), OPEN = chip('Open', 'mut');

const filing = () => `
  <div class="p-head"><h4>Filing status</h4><p>Read from the work itself: done only with its proof on file.</p></div>
  <div class="p-tabs"><span class="on">GSTR-3B (monthly)</span><span>TDS quarterly</span><span>GSTR-1</span><span>Income-tax return</span></div>
  <div class="p-tiles">${tile('8 of 26', 'Filed · Oct 2026')}${tile('18', 'Left to do')}${tile('1', 'Day to the due date', 'warn')}${tile('18', 'Needed a day', 'warn')}</div>
  <table class="p-table">
    <thead><tr><th>Client</th><th>Jul</th><th>Aug</th><th>Sep</th><th>Oct · due 20 Nov</th></tr></thead>
    <tbody>
      ${[
        ['Lakshmi Textiles', FILED, FILED, FILED, REVIEW],
        ['Balaji Hardware', FILED, FILED, FILED, AWAIT],
        ['Anbu Traders', FILED, FILED, FILED, PARTNER],
        ['Sundar Exports', FILED, FILED, FILED, FILED],
        ['Meena Agencies', FILED, FILED, REVIEW, AWAIT],
        ['Kaveri Foods', FILED, FILED, FILED, OPEN],
      ].map((r) => `<tr><td><b>${r[0]}</b><small>33XXXXX0000X1Z5</small></td>${r.slice(1).map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}
    </tbody>
  </table>`;

const today = () => `
  <div class="p-head"><h4>Good morning, Arun</h4><p>Partner · Tuesday 3 November</p></div>
  <div class="p-ask"><span>Which of my deliverables are late?</span><b>Ask</b></div>
  <div class="p-tiles four">${tile('6', 'To sign')}${tile('2', 'Late', 'warn')}${tile('14', 'Due in 7 days')}${tile('3', 'From the department')}</div>
  <div class="p-tiles four">${tile('1', 'Notice reply open', 'warn')}${tile('₹1,82,450', 'Signed off, not billed', 'money')}${tile('₹42,300', 'Owed, past due')}${tile('92%', 'On time this quarter', 'good')}</div>
  <h5>Waiting on you, late first</h5>
  <ul class="p-rows">
    <li><b>Tax audit report · Sundar Exports</b><span>Sign off</span>${chip('Late 1 day', 'red')}</li>
    <li><b>GSTR-3B Oct · Anbu Traders</b><span>Sign off</span>${chip('Due in 1 day', 'amb')}</li>
    <li><b>Notice AY 2023-24 · Balaji Hardware</b><span>Review the reply</span>${chip('Due 12 Nov', 'amb')}</li>
  </ul>`;

const department = () => `
  <div class="p-head"><h4>From the department</h4><p>Newest first. A filed return's proof goes on its period by itself; a notice is listed to act on.</p></div>
  <div class="p-tabs"><span class="on">To act on (2)</span><span>Filed and processed (41)</span><span>Other news (6)</span></div>
  <ul class="p-rows tall">
    <li><span class="p-src">ITBA</span><b>Notice for AY 2023-24<small>Balaji Hardware · reply by 12 Nov 2026</small></b>${chip('Job opened', 'blu')}</li>
    <li><span class="p-src">CPC</span><b>Refund failed: account not validated<small>Mohan Reddy · AY 2026-27</small></b>${chip('Open a job', 'amb')}</li>
    <li><span class="p-src">Insight</span><b>Return not verified: 30 days to verify<small>Fatima Khan · AY 2026-27</small></b>${chip('Reminded', 'vio')}</li>
    <li><span class="p-src">CPC</span><b>Intimation: return processed<small>Ramesh Kumar · AY 2026-27 · proof added</small></b>${chip('Done', 'ok')}</li>
    <li><span class="p-src">GSTN</span><b>GSTR-3B filed, ARN read from the text<small>Lakshmi Textiles · Sep 2026 · proof added</small></b>${chip('Done', 'ok')}</li>
  </ul>`;

const row = (mark: string, cls: string, t: string, s: string) => `<li class="p-doc ${cls}"><i>${mark}</i><b>${t}<small>${s}</small></b></li>`;
const documents = () => `
  <div class="p-head"><h4>Income-tax return · Ramesh Kumar</h4><p>FY 2025-26 · Prepared by Nisha · Reviewed by Deepa · Signed by Arun</p></div>
  <div class="p-tiles">${tile('5 of 6', 'Documents in')}${tile('10', 'Files read by themselves', 'good')}${tile('1', 'Set aside')}</div>
  <ul class="p-rows docs">
    ${row('✓', 'in', 'Form 16', 'Read from WhatsApp. Another employer’s Form 16 for FY 2024-25 set aside: wrong year.')}
    ${row('✓', 'in', 'Bank statement, Apr–Dec and Jan–Mar', 'Two parts joined, whole year covered. A copy sent twice kept once.')}
    ${row('✓', 'in', 'Sale and purchase deeds', 'Asked because the client said a property was sold this year.')}
    ${row('🔒', 'lock', 'Investment proofs', 'Locked PDF. Password typed once, then read.')}
    ${row('…', 'wait', 'Home-loan interest certificate', 'Still with the client. Reminder set.')}
  </ul>
  <div class="p-foot">Salary, TDS and interest are filled into the workpaper, each saying which file it came from.</div>`;

const billing = () => `
  <div class="p-head"><h4>Fees</h4><p>Signed-off work stays here until it is invoiced.</p></div>
  <div class="p-tiles">${tile('₹1,82,450', 'Signed off, not billed', 'money')}${tile('10', 'Pieces of work')}${tile('₹42,300', 'Owed, past due')}</div>
  <table class="p-table">
    <thead><tr><th>Work</th><th>Client</th><th>Hours</th><th>Fee</th><th></th></tr></thead>
    <tbody>
      ${[
        ['Tax audit FY 2025-26', 'Sundar Exports', '38', '₹45,000'],
        ['GSTR-3B Sep 2026', 'Lakshmi Textiles', '3', '₹1,500'],
        ['ITR FY 2025-26', 'Ramesh Kumar', '4', '₹3,500'],
        ['TDS Q2', 'Naveen Prasad', '2', '₹2,000'],
      ].map((r) => `<tr><td><b>${r[0]}</b></td><td>${r[1]}</td><td>${r[2]}</td><td><b>${r[3]}</b></td><td><span class="p-btn">Bill</span></td></tr>`).join('')}
    </tbody>
  </table>
  <ul class="p-rows"><li><b>Client approved the figures and paid by UPI<small>Ramesh Kumar · ₹3,500 · reference recorded</small></b>${chip('Ready to file', 'ok')}</li></ul>`;

const workbook = () => `
  <div class="p-head"><h4>Workbook · today</h4><p>Marked from the computer sign-in and the phone’s location.</p></div>
  <div class="p-tiles four">${tile('18', 'Present', 'good')}${tile('1', 'Late')}${tile('2', 'On leave')}${tile('3', 'At client site')}</div>
  <table class="p-table">
    <thead><tr><th>Person</th><th>In</th><th>Active</th><th>Idle</th><th>Worked on</th></tr></thead>
    <tbody>
      ${[
        ['Karthik Raja', '9:42', '5h 10m', '22m', 'Lakshmi Textiles · GST'],
        ['Nisha Thomas', '9:50', '4h 55m', '18m', 'Ramesh Kumar · ITR'],
        ['Vignesh Mani', '10:05', '4h 20m', '31m', 'Sundar Exports · audit'],
        ['Priya Sundar', '9:35', '5h 25m', '14m', 'Naveen Prasad · TDS'],
        ['Mohan Das', '10:11', '3h 48m', '26m', 'Balaji Hardware · notice'],
      ].map((r) => `<tr><td><b>${r[0]}</b></td>${r.slice(1).map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}
    </tbody>
  </table>`;

export const PANELS: Record<string, () => string> = { filing, today, department, documents, billing, workbook };
